import { useState, useEffect } from 'react';
import API from '../lib/api';
import CustomReportsTab from '../components/CustomReportsTab';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Users, ShieldCheck, Key, Cube, Clock, Warning, CheckCircle, TrendUp, ChartPie, ChartBar, Gauge, ArrowUp, ArrowDown, Target, Lightning, UserPlus, ArrowsLeftRight, UserMinus, CloudArrowUp, ClipboardText, Ticket } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart as RechartsPie, Pie, Cell, LineChart, Line, Legend, AreaChart, Area, RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis, ComposedChart } from 'recharts';

const COLORS = ['#f97316', '#3b82f6', '#10b981', '#8b5cf6', '#ec4899', '#f59e0b', '#06b6d4', '#84cc16'];

export default function ReportsPage() {
  const [data, setData] = useState(null);
  const [compliance, setCompliance] = useState(null);
  const [trends, setTrends] = useState(null);
  const [entitlements, setEntitlements] = useState(null);
  const [appAccess, setAppAccess] = useState(null);
  const [risk, setRisk] = useState(null);
  const [certSummary, setCertSummary] = useState(null);
  const [lifecycleSummary, setLifecycleSummary] = useState(null);
  const [provisioningSummary, setProvisioningSummary] = useState(null);
  const [certDetailed, setCertDetailed] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadData = async () => {
      try {
        const [dashRes, compRes, trendRes, entRes, appRes, riskRes, certRes, lcRes, provRes, certDetailRes] = await Promise.all([
          API.get('/reports/dashboard'),
          API.get('/reports/compliance'),
          API.get('/reports/access-trends').catch(() => ({ data: { trends: [] } })),
          API.get('/reports/entitlements-distribution').catch(() => ({ data: { by_type: [], by_source: [], top_entitlements: [] } })),
          API.get('/reports/application-access').catch(() => ({ data: { applications: [] } })),
          API.get('/reports/risk-summary').catch(() => ({ data: { overall_score: 0, risk_level: 'good', breakdown: [] } })),
          API.get('/reports/certification-summary').catch(() => ({ data: { by_status: {}, recent_campaigns: [] } })),
          API.get('/reports/lifecycle-summary').catch(() => ({ data: { total_rules: 0, total_events: 0, events_by_type: {}, top_rules: [], actions_breakdown: [], event_trend: [] } })),
          API.get('/reports/provisioning-summary').catch(() => ({ data: { total_tickets: 0, tickets_by_status: {}, tickets_by_type: [], last_sync: null, ticket_trend: [] } })),
          API.get('/reports/certification-detailed').catch(() => ({ data: { summary: {}, campaigns: [], reviewer_stats: [] } }))
        ]);
        setData(dashRes.data);
        setCompliance(compRes.data);
        setTrends(trendRes.data);
        setEntitlements(entRes.data);
        setAppAccess(appRes.data);
        setRisk(riskRes.data);
        setCertSummary(certRes.data);
        setLifecycleSummary(lcRes.data);
        setProvisioningSummary(provRes.data);
        setCertDetailed(certDetailRes.data);
      } catch (err) {
        toast.error('Failed to load reports');
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  if (!data) return null;

  const assignmentTypeData = Object.entries(data.assignment_by_type).map(([name, value]) => ({
    name: name.charAt(0).toUpperCase() + name.slice(1),
    value
  }));

  return (
    <div className="p-6 space-y-6" data-testid="reports-page">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-navy-900" style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}>Reports & Analytics</h1>
          <p className="text-sm text-navy-500 mt-1">Comprehensive overview of identity governance metrics</p>
        </div>
        {risk && (
          <RiskScoreBadge score={risk.overall_score} level={risk.risk_level} />
        )}
      </div>

      <Tabs defaultValue="overview" className="space-y-4">
        <TabsList className="bg-slate-100 rounded-lg p-1 h-auto">
          <TabsTrigger value="overview" className="rounded-md text-xs px-4 py-2 data-[state=active]:bg-white data-[state=active]:shadow-sm">Overview</TabsTrigger>
          <TabsTrigger value="access" className="rounded-md text-xs px-4 py-2 data-[state=active]:bg-white data-[state=active]:shadow-sm">Access Analytics</TabsTrigger>
          <TabsTrigger value="applications" className="rounded-md text-xs px-4 py-2 data-[state=active]:bg-white data-[state=active]:shadow-sm">Applications</TabsTrigger>
          <TabsTrigger value="compliance" className="rounded-md text-xs px-4 py-2 data-[state=active]:bg-white data-[state=active]:shadow-sm">Compliance</TabsTrigger>
          <TabsTrigger value="risk" className="rounded-md text-xs px-4 py-2 data-[state=active]:bg-white data-[state=active]:shadow-sm">Risk</TabsTrigger>
          <TabsTrigger value="trends" className="rounded-md text-xs px-4 py-2 data-[state=active]:bg-white data-[state=active]:shadow-sm">Trends</TabsTrigger>
          <TabsTrigger value="lifecycle" className="rounded-md text-xs px-4 py-2 data-[state=active]:bg-white data-[state=active]:shadow-sm" data-testid="tab-lifecycle-report">Lifecycle</TabsTrigger>
          <TabsTrigger value="certifications" className="rounded-md text-xs px-4 py-2 data-[state=active]:bg-white data-[state=active]:shadow-sm" data-testid="tab-cert-report">Certifications</TabsTrigger>
          <TabsTrigger value="provisioning" className="rounded-md text-xs px-4 py-2 data-[state=active]:bg-white data-[state=active]:shadow-sm" data-testid="tab-prov-report">Provisioning</TabsTrigger>
          <TabsTrigger value="custom" className="rounded-md text-xs px-4 py-2 data-[state=active]:bg-orange-500 data-[state=active]:text-white data-[state=active]:shadow-sm" data-testid="tab-custom-reports">Custom Reports</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-4">
          {/* Summary Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
            <MetricCard icon={Users} label="Total Identities" value={data.summary.identity_count} />
            <MetricCard icon={Users} label="Active Identities" value={data.summary.active_identities} color="emerald" />
            <MetricCard icon={ShieldCheck} label="Roles" value={data.summary.role_count} />
            <MetricCard icon={Key} label="Entitlements" value={data.summary.entitlement_count} />
            <MetricCard icon={Cube} label="Applications" value={data.summary.app_count} />
            <MetricCard icon={CheckCircle} label="Assignments" value={data.summary.active_assignments} color="blue" />
          </div>

          {/* Alert Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <AlertCard 
              icon={Clock} 
              label="Expiring Soon" 
              value={data.summary.expiring_soon} 
              description="Assignments expiring in 30 days"
              color="amber"
            />
            <AlertCard 
              icon={TrendUp} 
              label="Pending Requests" 
              value={data.summary.pending_requests} 
              description="Access requests awaiting approval"
              color="blue"
            />
            <AlertCard 
              icon={Warning} 
              label="SoD Violations" 
              value={data.summary.sod_violations} 
              description="Open separation of duties violations"
              color="red"
            />
          </div>

          {/* Charts Row */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card className="rounded-lg">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-semibold text-navy-700 flex items-center gap-2">
                  <ChartBar size={16} weight="duotone" className="text-orange-500" />
                  Identities by Department
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={data.identities_by_department} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis type="number" tick={{ fontSize: 11 }} />
                    <YAxis dataKey="department" type="category" width={100} tick={{ fontSize: 11 }} />
                    <Tooltip contentStyle={{ borderRadius: '8px', border: '1px solid #e2e8f0' }} />
                    <Bar dataKey="count" fill="#f97316" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            <Card className="rounded-lg">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-semibold text-navy-700 flex items-center gap-2">
                  <ChartPie size={16} weight="duotone" className="text-blue-500" />
                  Assignment Types
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={250}>
                  <RechartsPie>
                    <Pie
                      data={assignmentTypeData}
                      cx="50%"
                      cy="50%"
                      innerRadius={50}
                      outerRadius={80}
                      paddingAngle={2}
                      dataKey="value"
                      label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                      labelLine={{ stroke: '#94a3b8', strokeWidth: 1 }}
                    >
                      {assignmentTypeData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip contentStyle={{ borderRadius: '8px', border: '1px solid #e2e8f0' }} />
                  </RechartsPie>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="access" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card className="rounded-lg">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-semibold text-navy-700">Top 10 Roles by Assignment</CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={data.top_roles} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis type="number" tick={{ fontSize: 11 }} />
                    <YAxis dataKey="role" type="category" width={120} tick={{ fontSize: 11 }} />
                    <Tooltip contentStyle={{ borderRadius: '8px', border: '1px solid #e2e8f0' }} />
                    <Bar dataKey="count" fill="#3b82f6" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            <Card className="rounded-lg">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-semibold text-navy-700">Identity Lifecycle States</CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <RechartsPie>
                    <Pie
                      data={data.identities_by_lifecycle}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={100}
                      paddingAngle={2}
                      dataKey="count"
                      nameKey="state"
                      label={({ state, percent }) => `${state} ${(percent * 100).toFixed(0)}%`}
                    >
                      {data.identities_by_lifecycle.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip contentStyle={{ borderRadius: '8px', border: '1px solid #e2e8f0' }} />
                    <Legend />
                  </RechartsPie>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>

          {entitlements && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              <Card className="rounded-lg">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-semibold text-navy-700">Entitlements by Type</CardTitle>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={200}>
                    <RechartsPie>
                      <Pie
                        data={entitlements.by_type}
                        cx="50%"
                        cy="50%"
                        outerRadius={70}
                        dataKey="count"
                        nameKey="type"
                        label={({ type }) => type}
                      >
                        {entitlements.by_type.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </RechartsPie>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              <Card className="rounded-lg">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-semibold text-navy-700">Entitlements by Source</CardTitle>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={200}>
                    <BarChart data={entitlements.by_source}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis dataKey="source" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} />
                      <Tooltip />
                      <Bar dataKey="count" fill="#8b5cf6" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              <Card className="rounded-lg">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-semibold text-navy-700">Top Entitlements (by Role Usage)</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {entitlements.top_entitlements.slice(0, 5).map((ent) => (
                      <div key={ent.name} className="flex items-center justify-between">
                        <span className="text-sm text-navy-700 truncate flex-1">{ent.name}</span>
                        <Badge variant="secondary" className="ml-2 rounded-full text-[10px]">{ent.roles_count} roles</Badge>
                      </div>
                    ))}
                    {entitlements.top_entitlements.length === 0 && (
                      <p className="text-sm text-navy-400 text-center py-4">No entitlement data</p>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          <Card className="rounded-lg">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold text-navy-700">Recent Activity by Action Type</CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={data.recent_activity_by_action.slice(0, 10)}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="action" tick={{ fontSize: 10 }} angle={-45} textAnchor="end" height={60} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip contentStyle={{ borderRadius: '8px', border: '1px solid #e2e8f0' }} />
                  <Bar dataKey="count" fill="#10b981" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="applications" className="space-y-4">
          {appAccess && appAccess.applications.length > 0 ? (
            <>
              <Card className="rounded-lg">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-semibold text-navy-700">Application Access Distribution</CardTitle>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={350}>
                    <ComposedChart data={appAccess.applications.slice(0, 10)} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis type="number" tick={{ fontSize: 11 }} />
                      <YAxis dataKey="app_name" type="category" width={120} tick={{ fontSize: 11 }} />
                      <Tooltip contentStyle={{ borderRadius: '8px', border: '1px solid #e2e8f0' }} />
                      <Legend />
                      <Bar dataKey="users" name="Users" fill="#f97316" radius={[0, 4, 4, 0]} />
                      <Bar dataKey="roles" name="Roles" fill="#3b82f6" radius={[0, 4, 4, 0]} />
                    </ComposedChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {appAccess.applications.slice(0, 6).map((app) => (
                  <Card key={app.name} className="rounded-lg">
                    <CardContent className="pt-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center">
                          <Cube size={20} weight="duotone" className="text-navy-600" />
                        </div>
                        <div className="flex-1">
                          <p className="font-semibold text-navy-900">{app.app_name}</p>
                          <div className="flex items-center gap-3 mt-1">
                            <span className="text-xs text-navy-500">{app.users} users</span>
                            <span className="text-xs text-navy-400">•</span>
                            <span className="text-xs text-navy-500">{app.roles} roles</span>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </>
          ) : (
            <Card className="rounded-lg">
              <CardContent className="py-12 text-center">
                <Cube size={40} weight="duotone" className="mx-auto text-navy-300 mb-3" />
                <p className="text-navy-500">No application data available</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="compliance" className="space-y-4">
          {compliance && (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <ComplianceCard 
                  label="Orphan Accounts" 
                  value={compliance.orphan_accounts} 
                  status={compliance.orphan_accounts === 0 ? 'good' : 'warning'}
                  description="Active identities with no role assignments"
                />
                <ComplianceCard 
                  label="Expired Assignments" 
                  value={compliance.expired_active_assignments} 
                  status={compliance.expired_active_assignments === 0 ? 'good' : 'critical'}
                  description="Still marked active past expiry date"
                />
                <ComplianceCard 
                  label="Roles Without Owners" 
                  value={`${compliance.roles_without_owners}/${compliance.total_roles}`}
                  status={compliance.roles_without_owners === 0 ? 'good' : 'warning'}
                  description="Roles missing designated owners"
                />
                <ComplianceCard 
                  label="No Manager Assigned" 
                  value={compliance.identities_without_managers} 
                  status={compliance.identities_without_managers < 10 ? 'good' : 'warning'}
                  description="Active identities without managers"
                />
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <Card className="rounded-lg">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-semibold text-navy-700">Certification Coverage</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center gap-6">
                      <div className="relative w-32 h-32">
                        <svg className="w-full h-full transform -rotate-90">
                          <circle cx="64" cy="64" r="56" stroke="#e2e8f0" strokeWidth="12" fill="none" />
                          <circle 
                            cx="64" cy="64" r="56" 
                            stroke="#10b981" 
                            strokeWidth="12" 
                            fill="none"
                            strokeDasharray={`${compliance.certification_rate * 3.52} 352`}
                            strokeLinecap="round"
                          />
                        </svg>
                        <div className="absolute inset-0 flex items-center justify-center">
                          <span className="text-2xl font-bold text-navy-900">{compliance.certification_rate}%</span>
                        </div>
                      </div>
                      <div>
                        <p className="text-sm text-navy-600">
                          <span className="font-semibold text-navy-900">{compliance.certified_assignments}</span> of{' '}
                          <span className="font-semibold text-navy-900">{compliance.total_active_assignments}</span> assignments certified
                        </p>
                        <p className="text-xs text-navy-400 mt-1">Run certification campaigns to improve coverage</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="rounded-lg">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-semibold text-navy-700">SoD Compliance</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-navy-600">Active Policies</span>
                        <span className="font-semibold text-navy-900">{compliance.sod_policies_count}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-navy-600">Open Violations</span>
                        <span className={`font-semibold ${compliance.open_sod_violations > 0 ? 'text-red-600' : 'text-emerald-600'}`}>
                          {compliance.open_sod_violations}
                        </span>
                      </div>
                      <div className="pt-2 border-t border-slate-100">
                        <p className={`text-sm font-medium ${compliance.open_sod_violations === 0 ? 'text-emerald-600' : 'text-amber-600'}`}>
                          {compliance.open_sod_violations === 0 ? '✓ No active violations' : '⚠ Violations require attention'}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {certSummary && certSummary.recent_campaigns.length > 0 && (
                <Card className="rounded-lg">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-semibold text-navy-700">Recent Certification Campaigns</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {certSummary.recent_campaigns.map((camp) => (
                        <div key={camp.name} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                          <div>
                            <p className="font-medium text-navy-900">{camp.name}</p>
                            <p className="text-xs text-navy-500">{camp.total_items} items total</p>
                          </div>
                          <div className="flex items-center gap-3">
                            <Badge className={`rounded-full text-[10px] ${
                              camp.status === 'completed' ? 'bg-emerald-100 text-emerald-700' :
                              camp.status === 'active' ? 'bg-blue-100 text-blue-700' :
                              'bg-slate-100 text-navy-600'
                            }`}>
                              {camp.status}
                            </Badge>
                            <div className="text-right">
                              <p className="text-xs text-emerald-600">{camp.certified_count} certified</p>
                              <p className="text-xs text-red-600">{camp.revoked_count} revoked</p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </>
          )}
        </TabsContent>

        <TabsContent value="risk" className="space-y-4">
          {risk && (
            <>
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                <Card className="rounded-lg lg:col-span-1">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-semibold text-navy-700">Overall Risk Score</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex flex-col items-center py-4">
                      <div className="relative w-40 h-40">
                        <svg className="w-full h-full transform -rotate-90">
                          <circle cx="80" cy="80" r="70" stroke="#e2e8f0" strokeWidth="12" fill="none" />
                          <circle 
                            cx="80" cy="80" r="70" 
                            stroke={risk.risk_level === 'critical' ? '#ef4444' : risk.risk_level === 'warning' ? '#f59e0b' : '#10b981'}
                            strokeWidth="12" 
                            fill="none"
                            strokeDasharray={`${risk.overall_score * 4.4} 440`}
                            strokeLinecap="round"
                          />
                        </svg>
                        <div className="absolute inset-0 flex flex-col items-center justify-center">
                          <span className={`text-4xl font-black ${
                            risk.risk_level === 'critical' ? 'text-red-600' : 
                            risk.risk_level === 'warning' ? 'text-amber-600' : 'text-emerald-600'
                          }`}>{risk.overall_score}</span>
                          <span className="text-xs text-navy-500 uppercase tracking-wider">Risk Score</span>
                        </div>
                      </div>
                      <Badge className={`mt-4 rounded-full ${
                        risk.risk_level === 'critical' ? 'bg-red-100 text-red-700' : 
                        risk.risk_level === 'warning' ? 'bg-amber-100 text-amber-700' : 
                        'bg-emerald-100 text-emerald-700'
                      }`}>
                        {risk.risk_level.toUpperCase()} RISK
                      </Badge>
                    </div>
                  </CardContent>
                </Card>

                <Card className="rounded-lg lg:col-span-2">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-semibold text-navy-700">Risk Breakdown</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {risk.breakdown.map((item) => (
                        <div key={item.label} className="flex items-center gap-4">
                          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                            item.severity === 'critical' ? 'bg-red-100' :
                            item.severity === 'warning' ? 'bg-amber-100' :
                            item.severity === 'info' ? 'bg-blue-100' : 'bg-emerald-100'
                          }`}>
                            <Warning size={18} weight="duotone" className={
                              item.severity === 'critical' ? 'text-red-600' :
                              item.severity === 'warning' ? 'text-amber-600' :
                              item.severity === 'info' ? 'text-blue-600' : 'text-emerald-600'
                            } />
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center justify-between">
                              <span className="font-medium text-navy-900">{item.category}</span>
                              <span className="text-xl font-bold text-navy-900">{item.count}</span>
                            </div>
                            <div className="mt-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                              <div 
                                className={`h-full rounded-full ${
                                  item.severity === 'critical' ? 'bg-red-500' :
                                  item.severity === 'warning' ? 'bg-amber-500' :
                                  item.severity === 'info' ? 'bg-blue-500' : 'bg-emerald-500'
                                }`}
                                style={{ width: `${Math.min(100, item.impact)}%` }}
                              />
                            </div>
                            <p className="text-xs text-navy-400 mt-1">Impact: {item.impact} points</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </div>
            </>
          )}
        </TabsContent>

        <TabsContent value="trends" className="space-y-4">
          <Card className="rounded-lg">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold text-navy-700">Role Assignment Trend (Last 30 Days)</CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <AreaChart data={data.assignment_trend}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis 
                    dataKey="date" 
                    tick={{ fontSize: 10 }} 
                    tickFormatter={(v) => new Date(v).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                    interval={4}
                  />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip 
                    labelFormatter={(v) => new Date(v).toLocaleDateString()}
                    formatter={(value) => [value, 'Assignments']}
                    contentStyle={{ borderRadius: '8px', border: '1px solid #e2e8f0' }}
                  />
                  <Area type="monotone" dataKey="count" stroke="#f97316" fill="#fed7aa" strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {trends && trends.trends.length > 0 && (
            <Card className="rounded-lg">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-semibold text-navy-700">Access Request Activity (Last 30 Days)</CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={trends.trends}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis 
                      dataKey="date" 
                      tick={{ fontSize: 10 }} 
                      tickFormatter={(v) => new Date(v).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                      interval={4}
                    />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip 
                      labelFormatter={(v) => new Date(v).toLocaleDateString()}
                      contentStyle={{ borderRadius: '8px', border: '1px solid #e2e8f0' }}
                    />
                    <Legend />
                    <Line type="monotone" dataKey="requests" name="New Requests" stroke="#3b82f6" strokeWidth={2} dot={false} />
                    <Line type="monotone" dataKey="approvals" name="Processed" stroke="#10b981" strokeWidth={2} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* ========== LIFECYCLE REPORT ========== */}
        <TabsContent value="lifecycle" className="space-y-4">
          {lifecycleSummary && (
            <>
              {/* Summary Metrics */}
              <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
                <MetricCard icon={Lightning} label="Total Rules" value={lifecycleSummary.total_rules} />
                <MetricCard icon={CheckCircle} label="Active Rules" value={lifecycleSummary.active_rules} color="emerald" />
                <MetricCard icon={ChartBar} label="Total Events" value={lifecycleSummary.total_events} color="blue" />
                <MetricCard icon={UserPlus} label="Joiners" value={lifecycleSummary.events_by_type?.joiner || 0} color="emerald" />
                <MetricCard icon={ArrowsLeftRight} label="Movers" value={lifecycleSummary.events_by_type?.mover || 0} color="blue" />
                <MetricCard icon={UserMinus} label="Leavers" value={lifecycleSummary.events_by_type?.leaver || 0} />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Actions Breakdown */}
                <Card>
                  <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><Lightning size={16} className="text-amber-500" /> Automation Actions Breakdown</CardTitle></CardHeader>
                  <CardContent>
                    {lifecycleSummary.actions_breakdown?.length > 0 ? (
                      <ResponsiveContainer width="100%" height={200}>
                        <BarChart data={lifecycleSummary.actions_breakdown} layout="vertical">
                          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                          <XAxis type="number" tick={{ fontSize: 11 }} />
                          <YAxis dataKey="action" type="category" tick={{ fontSize: 10 }} width={100} />
                          <Tooltip contentStyle={{ fontSize: 12 }} />
                          <Bar dataKey="completed" fill="#10b981" name="Completed" stackId="a" />
                          <Bar dataKey="skipped" fill="#94a3b8" name="Skipped" stackId="a" />
                          <Legend wrapperStyle={{ fontSize: 11 }} />
                        </BarChart>
                      </ResponsiveContainer>
                    ) : <p className="text-sm text-navy-400 text-center py-8">No actions recorded yet</p>}
                  </CardContent>
                </Card>

                {/* Top Rules */}
                <Card>
                  <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><ShieldCheck size={16} className="text-purple-500" /> Top Rules by Execution</CardTitle></CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {(lifecycleSummary.top_rules || []).length === 0 && <p className="text-sm text-navy-400 text-center py-8">No rules configured yet</p>}
                      {(lifecycleSummary.top_rules || []).map((rule, idx) => (
                        <div key={rule.name} className="flex items-center gap-3 p-2 bg-slate-50 rounded-sm">
                          <span className="text-xs font-mono text-navy-400 w-5">{idx + 1}</span>
                          <Badge className={`text-[9px] ${rule.event_type === 'joiner' ? 'bg-emerald-100 text-emerald-700' : rule.event_type === 'mover' ? 'bg-blue-100 text-blue-700' : 'bg-red-100 text-red-700'}`}>
                            {rule.event_type}
                          </Badge>
                          <span className="text-sm font-medium text-navy-700 flex-1 truncate">{rule.name}</span>
                          <span className="text-sm font-bold text-navy-900">{rule.execution_count}x</span>
                          {!rule.enabled && <Badge className="text-[9px] bg-slate-200 text-navy-500">Disabled</Badge>}
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Event Trend */}
              {lifecycleSummary.event_trend?.length > 0 && (
                <Card>
                  <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><TrendUp size={16} className="text-blue-500" /> Lifecycle Events Over Time</CardTitle></CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={220}>
                      <AreaChart data={lifecycleSummary.event_trend}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                        <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                        <YAxis tick={{ fontSize: 11 }} />
                        <Tooltip contentStyle={{ fontSize: 12 }} />
                        <Area type="monotone" dataKey="joiner" fill="#10b981" stroke="#10b981" fillOpacity={0.3} name="Joiners" />
                        <Area type="monotone" dataKey="mover" fill="#3b82f6" stroke="#3b82f6" fillOpacity={0.3} name="Movers" />
                        <Area type="monotone" dataKey="leaver" fill="#ef4444" stroke="#ef4444" fillOpacity={0.3} name="Leavers" />
                        <Legend wrapperStyle={{ fontSize: 11 }} />
                      </AreaChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              )}

              {/* Events by Trigger Source */}
              {lifecycleSummary.events_by_trigger?.length > 0 && (
                <Card>
                  <CardHeader className="pb-2"><CardTitle className="text-sm">Event Trigger Sources</CardTitle></CardHeader>
                  <CardContent>
                    <div className="flex items-center gap-4 flex-wrap">
                      {lifecycleSummary.events_by_trigger.map((s) => (
                        <div key={s.source} className="flex items-center gap-2 bg-slate-50 rounded-sm px-3 py-2">
                          <Badge className="text-[9px] bg-purple-100 text-purple-700">{s.source}</Badge>
                          <span className="text-sm font-bold text-navy-900">{s.count}</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </>
          )}
        </TabsContent>

        {/* ========== CERTIFICATIONS REPORT ========== */}
        <TabsContent value="certifications" className="space-y-4">
          {certDetailed && (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <MetricCard icon={ClipboardText} label="Campaigns" value={certDetailed.summary?.total_campaigns || 0} />
                <MetricCard icon={CheckCircle} label="Certified" value={certDetailed.summary?.total_certified || 0} color="emerald" />
                <MetricCard icon={Warning} label="Revoked" value={certDetailed.summary?.total_revoked || 0} />
                <MetricCard icon={Clock} label="Pending" value={certDetailed.summary?.total_pending || 0} color="blue" />
              </div>

              {/* Overall Completion */}
              <Card>
                <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><Gauge size={16} className="text-orange-500" /> Overall Certification Completion</CardTitle></CardHeader>
                <CardContent>
                  <div className="flex items-center gap-4">
                    <div className="relative w-20 h-20">
                      <svg className="w-20 h-20 -rotate-90" viewBox="0 0 36 36">
                        <path d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" fill="none" stroke="#e2e8f0" strokeWidth="3" />
                        <path d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                          fill="none" stroke="#f97316" strokeWidth="3" strokeDasharray={`${certDetailed.summary?.overall_completion || 0}, 100`} />
                      </svg>
                      <div className="absolute inset-0 flex items-center justify-center">
                        <span className="text-sm font-bold text-navy-900">{certDetailed.summary?.overall_completion || 0}%</span>
                      </div>
                    </div>
                    <div className="flex-1 grid grid-cols-3 gap-4">
                      <div className="text-center">
                        <p className="text-2xl font-bold text-emerald-600">{certDetailed.summary?.total_certified || 0}</p>
                        <p className="text-xs text-navy-500">Certified</p>
                      </div>
                      <div className="text-center">
                        <p className="text-2xl font-bold text-red-600">{certDetailed.summary?.total_revoked || 0}</p>
                        <p className="text-xs text-navy-500">Revoked</p>
                      </div>
                      <div className="text-center">
                        <p className="text-2xl font-bold text-amber-600">{certDetailed.summary?.total_pending || 0}</p>
                        <p className="text-xs text-navy-500">Pending</p>
                      </div>
                    </div>
                    {certDetailed.summary?.overdue_campaigns > 0 && (
                      <div className="bg-red-50 border border-red-200 rounded-sm px-4 py-3 text-center">
                        <p className="text-2xl font-bold text-red-600">{certDetailed.summary.overdue_campaigns}</p>
                        <p className="text-xs text-red-500 font-semibold">Overdue</p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Campaign Details Table */}
              <Card>
                <CardHeader className="pb-2"><CardTitle className="text-sm">Campaign Details</CardTitle></CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead><tr className="bg-slate-50">
                        <th className="text-left text-[10px] font-bold uppercase text-navy-500 px-3 py-2">Campaign</th>
                        <th className="text-left text-[10px] font-bold uppercase text-navy-500 px-3 py-2">Type</th>
                        <th className="text-center text-[10px] font-bold uppercase text-navy-500 px-3 py-2">Items</th>
                        <th className="text-center text-[10px] font-bold uppercase text-navy-500 px-3 py-2">Progress</th>
                        <th className="text-center text-[10px] font-bold uppercase text-navy-500 px-3 py-2">Due</th>
                        <th className="text-center text-[10px] font-bold uppercase text-navy-500 px-3 py-2">Status</th>
                      </tr></thead>
                      <tbody>
                        {(certDetailed.campaigns || []).map((c) => (
                          <tr key={c.id} className="border-t border-slate-100 hover:bg-slate-50">
                            <td className="px-3 py-2 font-medium text-navy-900">{c.name}</td>
                            <td className="px-3 py-2"><Badge className="text-[9px] bg-slate-100 text-navy-600">{c.type}</Badge></td>
                            <td className="px-3 py-2 text-center">{c.total_items}</td>
                            <td className="px-3 py-2">
                              <div className="flex items-center gap-2">
                                <div className="flex-1 bg-slate-200 rounded-full h-2 overflow-hidden">
                                  <div className="bg-orange-500 h-full rounded-full" style={{ width: `${c.completion_pct}%` }} />
                                </div>
                                <span className="text-xs font-medium w-10 text-right">{c.completion_pct}%</span>
                              </div>
                            </td>
                            <td className="px-3 py-2 text-center">
                              <span className={`text-xs ${c.is_overdue ? 'text-red-600 font-bold' : 'text-navy-600'}`}>
                                {c.due_date ? new Date(c.due_date).toLocaleDateString() : '-'}
                              </span>
                              {c.is_overdue && <Badge className="ml-1 text-[8px] bg-red-100 text-red-700">OVERDUE</Badge>}
                            </td>
                            <td className="px-3 py-2 text-center">
                              <Badge className={`text-[9px] ${c.status === 'active' ? 'bg-blue-100 text-blue-700' : c.status === 'completed' ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-navy-500'}`}>{c.status}</Badge>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>

              {/* Reviewer Stats */}
              {certDetailed.reviewer_stats?.length > 0 && (
                <Card>
                  <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><Users size={16} className="text-blue-500" /> Reviewer Performance</CardTitle></CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={220}>
                      <BarChart data={certDetailed.reviewer_stats}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                        <XAxis dataKey="reviewer" tick={{ fontSize: 10 }} />
                        <YAxis tick={{ fontSize: 11 }} />
                        <Tooltip contentStyle={{ fontSize: 12 }} />
                        <Bar dataKey="certified" fill="#10b981" name="Certified" stackId="a" />
                        <Bar dataKey="revoked" fill="#ef4444" name="Revoked" stackId="a" />
                        <Legend wrapperStyle={{ fontSize: 11 }} />
                      </BarChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              )}
            </>
          )}
        </TabsContent>

        {/* ========== PROVISIONING REPORT ========== */}
        <TabsContent value="provisioning" className="space-y-4">
          {provisioningSummary && (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <MetricCard icon={Ticket} label="Total Tickets" value={provisioningSummary.total_tickets} />
                <MetricCard icon={CheckCircle} label="Completed" value={provisioningSummary.tickets_by_status?.completed || 0} color="emerald" />
                <MetricCard icon={Clock} label="Open" value={provisioningSummary.tickets_by_status?.open || 0} color="blue" />
                <MetricCard icon={Warning} label="Failed" value={provisioningSummary.tickets_by_status?.failed || 0} />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Tickets by Type */}
                <Card>
                  <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><ChartPie size={16} className="text-orange-500" /> Tickets by Type</CardTitle></CardHeader>
                  <CardContent>
                    {provisioningSummary.tickets_by_type?.length > 0 ? (
                      <ResponsiveContainer width="100%" height={200}>
                        <RechartsPie>
                          <Pie data={provisioningSummary.tickets_by_type} dataKey="count" nameKey="type" cx="50%" cy="50%" outerRadius={80} label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`} labelLine={{ stroke: '#94a3b8' }}>
                            {provisioningSummary.tickets_by_type.map((entry, i) => <Cell key={entry.type} fill={COLORS[i % COLORS.length]} />)}
                          </Pie>
                          <Tooltip contentStyle={{ fontSize: 12 }} />
                        </RechartsPie>
                      </ResponsiveContainer>
                    ) : <p className="text-sm text-navy-400 text-center py-8">No tickets recorded</p>}
                  </CardContent>
                </Card>

                {/* Last Azure Sync */}
                <Card>
                  <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><CloudArrowUp size={16} className="text-blue-500" /> Last Azure AD Sync</CardTitle></CardHeader>
                  <CardContent>
                    {provisioningSummary.last_sync ? (
                      <div className="space-y-3">
                        <p className="text-xs text-navy-500">{new Date(provisioningSummary.last_sync.completed_at).toLocaleString()} ({provisioningSummary.last_sync.duration_seconds}s)</p>
                        <div className="grid grid-cols-3 gap-2">
                          {[
                            { label: 'Users Imported', value: provisioningSummary.last_sync.users_imported },
                            { label: 'Users Updated', value: provisioningSummary.last_sync.users_updated },
                            { label: 'Groups Imported', value: provisioningSummary.last_sync.groups_imported },
                            { label: 'Grants', value: provisioningSummary.last_sync.provisioned_grants },
                            { label: 'Revokes', value: provisioningSummary.last_sync.provisioned_revokes },
                            { label: 'Errors', value: provisioningSummary.last_sync.errors }
                          ].map((s) => (
                            <div key={s.label} className="bg-slate-50 rounded-sm p-2 text-center">
                              <p className={`text-lg font-bold ${s.label === 'Errors' && s.value > 0 ? 'text-red-600' : 'text-navy-900'}`}>{s.value}</p>
                              <p className="text-[9px] text-navy-500 uppercase">{s.label}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : <p className="text-sm text-navy-400 text-center py-8">No aggregation completed yet</p>}
                  </CardContent>
                </Card>
              </div>

              {/* Top Identities by Tickets */}
              {provisioningSummary.top_identities_by_tickets?.length > 0 && (
                <Card>
                  <CardHeader className="pb-2"><CardTitle className="text-sm">Top Identities by Provisioning Tickets</CardTitle></CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={200}>
                      <BarChart data={provisioningSummary.top_identities_by_tickets.slice(0, 8)} layout="vertical">
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                        <XAxis type="number" tick={{ fontSize: 11 }} />
                        <YAxis dataKey="identity" type="category" tick={{ fontSize: 10 }} width={120} />
                        <Tooltip contentStyle={{ fontSize: 12 }} />
                        <Bar dataKey="tickets" fill="#f97316" />
                      </BarChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              )}
            </>
          )}
        </TabsContent>

        {/* Custom Reports Tab */}
        <TabsContent value="custom">
          <CustomReportsTab />
        </TabsContent>

      </Tabs>
    </div>
  );
}

function RiskScoreBadge({ score, level }) {
  const colors = {
    critical: 'bg-red-100 text-red-700 border-red-200',
    warning: 'bg-amber-100 text-amber-700 border-amber-200',
    good: 'bg-emerald-100 text-emerald-700 border-emerald-200'
  };

  return (
    <div className={`flex items-center gap-2 px-4 py-2 rounded-lg border ${colors[level]}`}>
      <Gauge size={20} weight="duotone" />
      <div>
        <p className="text-xs font-medium uppercase tracking-wider">Risk Score</p>
        <p className="text-lg font-bold">{score}/100</p>
      </div>
    </div>
  );
}

function MetricCard({ icon: Icon, label, value, color = 'slate' }) {
  const colorClasses = {
    slate: 'text-navy-800',
    emerald: 'text-emerald-700',
    blue: 'text-blue-700',
    orange: 'text-orange-700'
  };

  return (
    <div className="bg-white border border-slate-200 rounded-lg p-4 hover:shadow-md transition-shadow">
      <div className="flex items-center gap-3">
        <div className={colorClasses[color]}>
          <Icon size={22} weight="duotone" />
        </div>
        <div>
          <p className="text-xl font-bold text-navy-900">{value?.toLocaleString()}</p>
          <p className="text-[10px] text-navy-500 uppercase tracking-wider">{label}</p>
        </div>
      </div>
    </div>
  );
}

function AlertCard({ icon: Icon, label, value, description, color }) {
  const colorClasses = {
    amber: 'bg-amber-50 border-amber-200 text-amber-600',
    blue: 'bg-blue-50 border-blue-200 text-blue-600',
    red: 'bg-red-50 border-red-200 text-red-600'
  };

  return (
    <div className={`rounded-lg border p-4 ${colorClasses[color]}`}>
      <div className="flex items-start gap-3">
        <Icon size={20} weight="duotone" />
        <div>
          <p className="text-2xl font-bold">{value}</p>
          <p className="text-sm font-medium">{label}</p>
          <p className="text-xs opacity-75 mt-1">{description}</p>
        </div>
      </div>
    </div>
  );
}

function ComplianceCard({ label, value, status, description }) {
  const statusClasses = {
    good: 'border-emerald-200 bg-emerald-50',
    warning: 'border-amber-200 bg-amber-50',
    critical: 'border-red-200 bg-red-50'
  };

  const statusIcon = {
    good: <CheckCircle size={16} weight="duotone" className="text-emerald-500" />,
    warning: <Warning size={16} weight="duotone" className="text-amber-500" />,
    critical: <Warning size={16} weight="duotone" className="text-red-500" />
  };

  return (
    <div className={`rounded-lg border p-4 ${statusClasses[status]}`}>
      <div className="flex items-start justify-between">
        <div>
          <p className="text-2xl font-bold text-navy-900">{value}</p>
          <p className="text-sm font-medium text-navy-700">{label}</p>
          <p className="text-xs text-navy-500 mt-1">{description}</p>
        </div>
        {statusIcon[status]}
      </div>
    </div>
  );
}
